import { tagColor, tagBackgroundColor } from '@/utils/tagTheme';
import type { LandingTheme } from './themeData';

/**
 * The four sample lines are FIXED across every tile so the tiles can be
 * compared honestly. Don't pull `new Date()` here; that would defeat the
 * point of the side-by-side.
 */
const SAMPLE_HEADER = 'today · Apr 27';

interface SampleLine {
  time: string;
  /** Plain text segment that comes before the optional tag highlight. */
  before: string;
  /** Tag name (no `#`); when present it is rendered with `tagColor`. */
  tag?: string;
  /** Plain text segment that comes after the tag highlight. */
  after?: string;
}

const SAMPLE_LINES: SampleLine[] = [
  { time: '09:14', before: 'morning standup notes' },
  { time: '11:47', before: 'shipped the ', tag: 'refactor', after: ' finally' },
  { time: '14:02', before: 'meeting was actually useful' },
  { time: '22:18', before: 'bedtime, wrote three pages' },
];

interface ThemePreviewTileProps {
  theme: LandingTheme;
  /**
   * Hide the figcaption with the theme name. Used inside the fanout, where
   * captions on every fanned card create noise; the active theme name is
   * shown once below the fan instead.
   */
  hideCaption?: boolean;
  /**
   * Compact horizontal layout: fewer sample lines, tighter padding. Used
   * inside the fanned `ThemeStack` so the stack reads as a horizontal
   * gallery instead of a portrait card.
   */
  compact?: boolean;
}

/**
 * Renders a single static editor preview using a theme's colors. The tile is
 * a `<figure>` with a visible `<figcaption>` carrying the theme name; the
 * inner sample content is `aria-hidden` because it is decorative repetition
 * across all 13 tiles.
 */
export function ThemePreviewTile({ theme, hideCaption = false, compact = false }: ThemePreviewTileProps) {
  // Slightly dim the timestamp column so the eye lands on the content first,
  // mirroring the iOS `mutedForegroundWithOpacity` (0.7) treatment.
  const timestampColor = withOpacity(theme.mutedForeground, 0.7);
  const lines = SAMPLE_LINES;

  return (
    <figure className="m-0 flex flex-col">
      <div
        aria-hidden
        className={`rounded-lg border ${compact ? 'p-3' : 'p-4'}`}
        style={{
          backgroundColor: theme.background,
          color: theme.foreground,
          borderColor: theme.border,
        }}
      >
        <div
          className="font-mono text-[10px] uppercase tracking-[0.14em]"
          style={{ color: theme.mutedForeground }}
        >
          {SAMPLE_HEADER}
        </div>

        <ul className={`list-none pl-0 text-sm leading-snug ${compact ? 'mt-2 space-y-1.5' : 'mt-3 space-y-2'}`}>
          {lines.map((line) => (
            <li key={line.time} className="flex items-baseline gap-3">
              <span
                className="shrink-0 font-mono text-[11px]"
                style={{ color: timestampColor }}
              >
                {line.time}
              </span>
              <span
                className="flex-1 break-words"
                style={{ color: theme.foreground }}
              >
                {line.before}
                {line.tag ? (
                  <span
                    className="rounded-sm font-medium"
                    style={{
                      color: tagColor(line.tag, 'light'),
                      backgroundColor: tagBackgroundColor(line.tag, 'light'),
                      padding: '0 0.3em',
                    }}
                  >
                    #{line.tag}
                  </span>
                ) : null}
                {line.after ?? ''}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {hideCaption ? (
        <figcaption className="sr-only">{theme.name}</figcaption>
      ) : (
        <figcaption className="mt-3 font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
          {theme.name}
        </figcaption>
      )}
    </figure>
  );
}

/**
 * Apply an alpha multiplier to a `#rrggbb` hex string. Used to dim the
 * timestamp column to ~70% of the theme's `mutedForeground`.
 */
function withOpacity(hex: string, alpha: number): string {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return hex;
  const value = match[1];
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default ThemePreviewTile;
